import { Box, Typography } from '@mui/material';
import { Timestamp } from 'firebase/firestore';
import type { ActivityLogChange, ActivityLogEntry } from '../../lib/types';

// Builds the human-readable verb phrase used in each activity-log row,
// switching on `action`. The phrasing intentionally reads as past tense so
// the timestamp + actor + verb compose into a complete sentence in the UI.
export const verbPhrase = (entry: ActivityLogEntry): string => {
  const e = entry.entityLabel || entry.entityRef;
  switch (entry.action) {
    case 'create':
      return `added ${entry.entity} ${e}`;
    case 'update':
      return `edited ${entry.entity} ${e}`;
    case 'delete':
      return `deleted ${entry.entity} ${e}`;
    case 'bulk-delete': {
      const count = (entry.meta?.count as number | undefined) ?? 0;
      const scope = (entry.meta?.scope as string | undefined) ?? 'records';
      return `wiped ${count} ${scope} from ${e}`;
    }
    case 'clear-labels': {
      const count = (entry.meta?.count as number | undefined) ?? 0;
      return `cleared cached labels for ${count} attendee${count === 1 ? '' : 's'} in ${e}`;
    }
    case 'check-in': {
      const name = entry.meta?.attendeeName ?? '(attendee)';
      const dir = entry.meta?.direction as string | undefined;
      if (dir === 'uncheck-in') {
        return `removed check-in for ${name} on ${e}`;
      }
      return `checked in ${name} on ${e}`;
    }
    case 'check-out': {
      const name = entry.meta?.attendeeName ?? '(attendee)';
      const dir = entry.meta?.direction as string | undefined;
      if (dir === 'uncheck-out') {
        return `removed check-out for ${name} on ${e}`;
      }
      return `checked out ${name} on ${e}`;
    }
    case 'activate':
      return `activated ${entry.entity} ${e}`;
    case 'archive':
      return `archived ${entry.entity} ${e}`;
    case 'unarchive':
      return `unarchived ${entry.entity} ${e}`;
    case 'enable-user':
      return `enabled user ${e}`;
    case 'disable-user':
      return `disabled user ${e}`;
    case 'grant-admin':
      return `granted admin to ${e}`;
    case 'revoke-admin':
      return `revoked admin from ${e}`;
    case 'grant-move-attendees':
      return `granted move-attendees permission to ${e}`;
    case 'revoke-move-attendees':
      return `revoked move-attendees permission from ${e}`;
    case 'auto-assign': {
      const count = (entry.meta?.count as number | undefined) ?? 0;
      return `auto-assigned ${count} attendees for ${e}`;
    }
    default:
      return `${entry.action} ${entry.entity} ${e}`;
  }
};

// Coarse "Xs/m/h/d ago" formatter used for the leading cell of each row.
// The tooltip on hover surfaces the absolute timestamp.
export const formatRelative = (ts: Timestamp | undefined): string => {
  if (!ts) return '';
  const now = Date.now();
  const ms = Math.max(0, now - ts.toMillis());
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
};

export const formatAbsolute = (ts: Timestamp | undefined): string =>
  ts ? ts.toDate().toLocaleString() : '';

// Formats arbitrary values surfaced from the activity log `changes` / `snapshot`
// / `meta` payloads. Strings render as-is; null / undefined surface as readable
// placeholders so the table doesn't show blanks.
export const renderValue = (v: unknown): string => {
  if (v === null) return '(none)';
  if (v === undefined) return '(unset)';
  if (v instanceof Timestamp) return v.toDate().toLocaleString();
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export const ChangesTable = ({
  changes,
}: {
  changes: Record<string, ActivityLogChange>;
}) => (
  <Box
    component='table'
    sx={{ width: '100%', borderCollapse: 'collapse', mt: 1 }}
  >
    <Box component='thead'>
      <Box component='tr'>
        <Box
          component='th'
          sx={{
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '0.75rem',
            color: 'text.secondary',
            pb: 0.5,
          }}
        >
          Field
        </Box>
        <Box
          component='th'
          sx={{
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '0.75rem',
            color: 'text.secondary',
            pb: 0.5,
          }}
        >
          From
        </Box>
        <Box
          component='th'
          sx={{
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '0.75rem',
            color: 'text.secondary',
            pb: 0.5,
          }}
        >
          To
        </Box>
      </Box>
    </Box>
    <Box component='tbody'>
      {Object.entries(changes).map(([k, v]) => (
        <Box component='tr' key={k}>
          <Box
            component='td'
            sx={{
              pr: 2,
              py: 0.25,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
            }}
          >
            {k}
          </Box>
          <Box
            component='td'
            sx={{
              pr: 2,
              py: 0.25,
              fontSize: '0.8rem',
              color: 'text.secondary',
            }}
          >
            {renderValue(v.from)}
          </Box>
          <Box component='td' sx={{ py: 0.25, fontSize: '0.8rem' }}>
            {renderValue(v.to)}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

export const KeyValueBlock = ({ obj }: { obj: Record<string, unknown> }) => (
  <Box sx={{ mt: 1 }}>
    {Object.entries(obj).map(([k, v]) => (
      <Box
        key={k}
        sx={{
          display: 'flex',
          gap: 1,
          fontSize: '0.8rem',
          py: 0.25,
        }}
      >
        <Box sx={{ fontFamily: 'monospace', minWidth: 160 }}>{k}</Box>
        <Typography component='span' sx={{ color: 'text.secondary' }}>
          {renderValue(v)}
        </Typography>
      </Box>
    ))}
  </Box>
);
